import React, { type ReactElement, useEffect } from "react";
import { useForm, useFormContext } from "react-hook-form";
import type {
  DefaultValues,
  FieldPath,
  Path,
  RegisterOptions,
  SubmitErrorHandler,
  SubmitHandler,
  UseFormProps,
  UseFormRegister,
  UseFormRegisterReturn,
} from "react-hook-form";

import {
  type FieldChange,
  type FormElement,
  type FormProps,
  type InputField,
  isLayout,
  type Layout,
} from "./types";
import Listener from "./listener";
import { InputComponent } from "./components";

import validator from "./validations";

export function renderFormElement(field: InputField, formProps: FormProps) {
  if (
    field.uiOptions?.visible &&
    !field.uiOptions?.visible(formProps.formValues)
  )
    return <></>;

  switch (field.type) {
    case "input":
      return <InputComponent field={field} formProps={formProps} />;
    case "render":
      return field.render(field, formProps);
    case "custom":
      return (
        <input
          type="text"
          {...formProps.register(field.name)}
          className={field?.uiOptions?.className}
          disabled={field?.uiOptions?.disabled}
          readOnly={field?.uiOptions?.readOnly}
        />
      );
  }
}

export default function useSourcelabForm<
  T extends FormElement,
  FormType extends Record<string, any>,
>({
  customRender,
  values,
  onSubmit,
  onInvalidForm,
  onFieldChange,
  resetAfterSubmit,
  nestedClassName,
  ...rest
}: UseFormProps<FormType> & {
  onSubmit?: SubmitHandler<FormType>;
  onInvalidForm?: SubmitErrorHandler<FormType>;
  values?: FormType;
  customRender?: (field: T, formProps: FormProps) => ReactElement | undefined;
  onFieldChange?: FieldChange<FormType>;
  resetAfterSubmit?: boolean;
  nestedClassName?: string;
  setFormValues?: (values: Map<string, unknown>) => void;
}) {
  const formContextMethods = useFormContext<FormType>();
  const formMethods = useForm<FormType>({
    defaultValues: values as DefaultValues<FormType>,
    ...rest,
  });
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, dirtyFields, isValid, isSubmitting },
    setValue,
    getValues,
    watch,
    reset,
  } = formContextMethods ?? formMethods;
  useEffect(() => reset(values), [values]);

  const listeners = new Listener();
  const watched: FormType = watch();

  const onSlFormChange = (fieldName: string, fieldValue: any) => {
    if (listeners.has(fieldName)) listeners.execute(fieldName, fieldValue);

    onFieldChange?.(
      fieldName,
      { old: watched[fieldName], new: fieldValue },
      watch()
    );
  };

  function renderLayout(field: Layout<T>, index: number) {
    if (field.render)
      return (
        <React.Fragment key={index.toString()}>
          {field.render({
            errors,
            formValues: { ...watched },
            setValue,
            reset,
          })}
        </React.Fragment>
      );
    if (field.children)
      return (
        <div
          className={field.className || nestedClassName}
          key={`nexted-${index}`}
        >
          {render(field.children as T[])}
        </div>
      );
  }

  function render(fields: T[]): (JSX.Element | undefined)[] {
    return fields.map((field, index) => {
      if (isLayout(field)) {
        return renderLayout(field as Layout<T>, index);
      }

      const formRegister: UseFormRegister<FormType> = <
        TFieldName extends FieldPath<FormType> = FieldPath<FormType>,
      >(
        name: TFieldName,
        options?: RegisterOptions<FormType, TFieldName>
      ): UseFormRegisterReturn<TFieldName> => {
        const validationRules = validator(
          { value: options?.value, label: field.uiOptions?.label },
          field.validations
        ) as Partial<RegisterOptions<FormType, TFieldName>>;
        const registerOptions = {
          ...validationRules,
          ...options,
        } as RegisterOptions<FormType, TFieldName>;
        const { onChange: reactHookOnChange, ...reactHookFormRegister } =
          register(name, registerOptions);

        const onChange = (event: {
          target: any;
          type?: any;
        }): Promise<void | boolean> => {
          const target = event.target as { name: Path<FormType>; value: any };
          onSlFormChange(target.name, target.value);

          return reactHookOnChange(event);
        };
        return { ...reactHookFormRegister, onChange };
      };

      const { register: _reactControlRegister, ...restControl } = control;
      const formProps = {
        register: formRegister,
        control: { ...restControl, register: formRegister },
        errors,
        error: errors[field.name]
          ? errors[field.name]?.message?.toString()
          : undefined,
        listeners,
        formValues: watched,
        setValue,
        getValues,
      };

      return (
        <React.Fragment key={`${field.name}-${index}`}>
          {customRender?.(field, formProps) ||
            renderFormElement(field, formProps)}
        </React.Fragment>
      );
    });
  }

  const formIsChanged = Object.keys(dirtyFields).length > 0;

  const onFormSubmit = handleSubmit(
    (data, event) =>
      Promise.resolve(onSubmit?.(data, event)).then(
        () => resetAfterSubmit && reset()
      ),
    onInvalidForm
  );
  return {
    render,
    onFormSubmit,
    currentValues: watched,
    setValue,
    formIsChanged,
    isValid,
    isSubmitting,
    reset,
  };
}
